import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Grid,
  ListItemText,
  Divider,
  Snackbar,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import ReplayIcon from '@mui/icons-material/Replay';

const DetailItem = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
      {value || 'N/A'}
    </Typography>
  </Box>
);

const ParticipantDetailModal = ({ participantId, open, onClose }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [participant, setParticipant] = useState(null);
  const [excursions, setExcursions] = useState([]);
  const [selectedExcursionId, setSelectedExcursionId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadingAction, setLoadingAction] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const fetchParticipantAndExcursions = useCallback(async () => {
    if (!participantId) return;
    setLoading(true);
    setError('');
    try {
      const [pRes, eRes] = await Promise.all([api.get(`/api/participants/${participantId}`), api.get('/api/excursions')]);
      setParticipant(pRes.data);
      setExcursions(eRes.data);
    } catch (err) {
      setError('Failed to fetch details.');
    } finally {
      setLoading(false);
    }
  }, [participantId]);

  useEffect(() => {
    if (open) {
      fetchParticipantAndExcursions();
    }
  }, [open, fetchParticipantAndExcursions]);

  const handleAction = async (action) => {
    if (action === 'reverse_clearance' && !window.confirm("Are you sure you want to reverse this financial clearance?")) return;
    setLoadingAction(action);
    try {
      let message = '';
      switch (action) {
        case 'clear_finance': await api.post('/api/finance/clear', { ParticipantID: participantId, PaymentMethod: 'On-site' }); message = 'Finance Cleared'; break;
        case 'reverse_clearance': await api.delete(`/api/finance/clearance/${participantId}`); message = 'Clearance Reversed'; break;
        case 'onboard': await api.post('/api/onboarding/complete', { ParticipantID: participantId }); message = 'Participant Onboarded'; break;
        case 'check_in': await api.post('/api/attendance/checkin', { ParticipantID: participantId }); message = 'Attendance Recorded'; break;
        default: return;
      }
      setSnackbar({ open: true, message: `${message} Successfully` });
      await fetchParticipantAndExcursions();
    } catch (err) {
      alert(`Action failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleBookExcursion = async () => {
    if (!selectedExcursionId) return;
    const excursionToBook = excursions.find(ex => ex.ExcursionID === selectedExcursionId);
    if (!excursionToBook) return;
    setLoadingAction('book_excursion');
    try {
      await api.post('/api/excursions/book', { ParticipantID: participantId, ExcursionID: selectedExcursionId, AmountPaid: excursionToBook.Cost });
      setSnackbar({ open: true, message: `Successfully booked for ${excursionToBook.SiteName}` });
      setSelectedExcursionId('');
      await fetchParticipantAndExcursions();
    } catch (err) {
      alert(`Booking failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleCancelExcursion = async () => {
    if (!window.confirm("Cancel this booking?")) return;
    setLoadingAction('cancel_excursion');
    try {
      await api.delete(`/api/excursions/booking/${participantId}`);
      setSnackbar({ open: true, message: "Booking cancelled." });
      await fetchParticipantAndExcursions();
    } catch (err) {
      alert(`Cancellation failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoadingAction(null);
    }
  };

  let activeStep = 0;
  if (participant?.IsFinanciallyCleared) activeStep = 1;
  if (participant?.IsOnboarded) activeStep = 2;

  const renderDueAmountDisplay = () => {
    if (!participant) return null;
    const dueAmount = parseFloat(participant.DueAmount);
    if (participant.IsFinanciallyCleared) {
      return <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'success.main', backgroundColor: '#edf7ed' }}><Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}><PriceCheckIcon color="success" /><Box><Typography fontWeight="bold" color="success.dark">Paid in Full</Typography><Typography variant="body2" color="success.dark">Participant is cleared.</Typography></Box></Box></Paper>;
    }
    if (dueAmount > 0) {
      return <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'error.main', backgroundColor: '#fdeded' }}><Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}><AttachMoneyIcon color="error" /><Box><Typography fontWeight="bold" color="error.dark">Outstanding Balance</Typography><Typography variant="h5" fontWeight="bold" color="error.dark">${dueAmount.toFixed(2)}</Typography></Box></Box></Paper>;
    }
    return <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'info.main', backgroundColor: '#e3f2fd' }}><Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}><PriceCheckIcon color="info" /><Box><Typography fontWeight="bold" color="info.dark">No Outstanding Balance</Typography><Typography variant="body2" color="info.dark">Ready for clearance.</Typography></Box></Box></Paper>;
  };

  return (
    <>
      <Dialog open={open} onClose={() => onClose(true)} fullWidth maxWidth="md" fullScreen={isMobile}>
        {loading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
        {participant && (
          <>
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {participant.FullName}
              <IconButton aria-label="close" onClick={() => onClose(true)}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={4}>
                <Grid item xs={12} md={7}>
                  <Typography variant="h6" gutterBottom>Participant Details</Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}><DetailItem label="Email Address" value={participant.EmailAddress} /></Grid>
                    <Grid item xs={12} sm={6}><DetailItem label="Phone Number" value={participant.PhoneNumber} /></Grid>
                    <Grid item xs={12} sm={8}><DetailItem label="Organization" value={participant.Organization} /></Grid>
                    <Grid item xs={12} sm={4}><DetailItem label="Country" value={participant.CountryName} /></Grid>
                    <Grid item xs={12} sm={4}><DetailItem label="Gender" value={participant.Gender} /></Grid>
                    <Grid item xs={12} sm={4}><DetailItem label="Delegate Type" value={participant.DelegateType} /></Grid>
                    <Grid item xs={12} sm={4}><DetailItem label="Passport No." value={participant.PassportNumber} /></Grid>
                  </Grid>
                  {(user?.role === 'Admin' || user?.role === 'Finance') && (
                    <>
                      <Divider sx={{ my: 2 }}><Chip icon={<ConfirmationNumberIcon />} label="Excursion Management" /></Divider>
                      {participant.RegisteredExcursions.length === 0 ? (
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Choose an Excursion</InputLabel>
                            <Select value={selectedExcursionId} label="Choose an Excursion" onChange={(e) => setSelectedExcursionId(e.target.value)}>
                              {excursions.map(ex => <MenuItem key={ex.ExcursionID} value={ex.ExcursionID}>{ex.SiteName} (${ex.Cost.toFixed(2)})</MenuItem>)}
                            </Select>
                          </FormControl>
                          <Button variant="outlined" onClick={handleBookExcursion} disabled={!selectedExcursionId || loadingAction} startIcon={loadingAction === 'book_excursion' ? <CircularProgress size={20} /> : null}>Book</Button>
                        </Box>
                      ) : (
                        <Paper variant="outlined" sx={{ p: 2, mt: 1, backgroundColor: '#f5f5f5' }}>
                          <Typography variant="subtitle1" fontWeight="bold">Booked Excursion:</Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <ListItemText primary={participant.RegisteredExcursions[0].SiteName} secondary={`Paid: $${participant.RegisteredExcursions[0].AmountPaid.toFixed(2)}`} />
                            <Button variant="text" color="error" size="small" onClick={handleCancelExcursion} disabled={loadingAction} startIcon={loadingAction === 'cancel_excursion' ? <CircularProgress size={20} /> : null}>Cancel</Button>
                          </Box>
                        </Paper>
                      )}
                    </>
                  )}
                </Grid>

                <Grid item xs={12} md={5}>
                  <Typography variant="h6" gutterBottom>Workflow Status</Typography>
                  {renderDueAmountDisplay()}
                  <Stepper activeStep={activeStep} orientation="vertical">
                    <Step>
                      <StepLabel StepIconComponent={() => activeStep > 0 ? <CheckCircleIcon color="success" /> : <PendingActionsIcon color={activeStep === 0 ? "primary" : "disabled"} />}>
                        <Typography fontWeight="bold">Financial Clearance</Typography>
                      </StepLabel>
                      <StepContent>
                        <Typography sx={{mb:1}}>{participant.IsFinanciallyCleared ? 'Cleared successfully.' : 'Confirm payment is settled.'}</Typography>
                        {(user?.role === 'Admin' || user?.role === 'Finance') && !participant.IsFinanciallyCleared && (
                          <Button variant="contained" onClick={() => handleAction('clear_finance')} disabled={loadingAction} startIcon={loadingAction === 'clear_finance' ? <CircularProgress size={20} /> : null}>
                            Clear Finance
                          </Button>
                        )}
                      </StepContent>
                    </Step>
                    <Step>
                      <StepLabel StepIconComponent={() => activeStep > 1 ? <CheckCircleIcon color="success" /> : <PendingActionsIcon color={activeStep === 1 ? "primary" : "disabled"} />}>
                        <Typography fontWeight="bold">Onboarding & Welcome Kit</Typography>
                      </StepLabel>
                      <StepContent>
                        <Typography sx={{mb:1}}>Provide badge, gifts, and materials.</Typography>
                        {(user?.role === 'Admin' || user?.role === 'Onboarding') && (
                          <Button variant="contained" onClick={() => handleAction('onboard')} disabled={!participant.IsFinanciallyCleared || participant.IsOnboarded || loadingAction} startIcon={loadingAction === 'onboard' ? <CircularProgress size={20} /> : null}>
                            Onboard Participant
                          </Button>
                        )}
                      </StepContent>
                    </Step>
                    <Step>
                      <StepLabel StepIconComponent={() => activeStep > 2 ? <CheckCircleIcon color="success" /> : <PendingActionsIcon color={activeStep === 2 ? "primary" : "disabled"} />}>
                        <Typography fontWeight="bold">Event Access</Typography>
                      </StepLabel>
                       <StepContent>
                        <Typography>Participant is fully checked in.</Typography>
                      </StepContent>
                    </Step>
                  </Stepper>
                  
                  <Divider sx={{ my: 2 }}><Chip label="Other Actions" /></Divider>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                    {(user?.role === 'Admin' || user?.role === 'Attendance') && (
                      <Button variant="contained" color="secondary" onClick={() => handleAction('check_in')} disabled={!participant.IsFinanciallyCleared || loadingAction} startIcon={loadingAction === 'check_in' ? <CircularProgress size={20} /> : null}>
                        Record Daily Attendance
                      </Button>
                    )}
                    {(user?.role === 'Admin') && participant.IsFinanciallyCleared && (
                      <Button variant="outlined" color="error" size="small" onClick={() => handleAction('reverse_clearance')} disabled={participant.IsOnboarded || loadingAction} startIcon={loadingAction === 'reverse_clearance' ? <CircularProgress size={20} /> : <ReplayIcon />}>
                        Reverse Finance Clearance
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions><Button onClick={() => onClose(true)}>Close</Button></DialogActions>
          </>
        )}
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ open: false, message: '' })} message={snackbar.message} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}/>
    </>
  );
};

export default ParticipantDetailModal;